import { Button, Divider, Form, Spin, Timeline, Typography } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import { administratorsSelector } from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import IApplication from '~Api/Application/IApplication';
import INote from '~Api/Application/INote';
import {
    applicationGetAction,
    applicationNotesAddAction,
    applicationNotesListAction,
} from '~Applications/actions';
import {
    applicationNotesSelector,
    applicationSelector,
} from '~Applications/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';

interface IMatch {
    applicationUuid: string;
}

interface IState {
    note: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    administrators: IDictionary<IAdministrator>;
    application: IApplication;
    notes: IDictionary<INote>;
}

interface IPropsDispatch {
    administratorsList: () => void;
    applicationGet: () => void;
    noteAdd: (note: string) => void;
    notesList: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Notes extends React.Component<Props> {
    public state: IState = {
        note: null,
    };

    constructor(props: Props) {
        super(props);

        this.onChangeNote = this.onChangeNote.bind(this);
        this.onClickAddNote = this.onClickAddNote.bind(this);
    }

    public componentDidMount() {
        const { administrators, application } = this.props;

        if (!administrators) {
            this.props.administratorsList();
        }

        if (!application) {
            this.props.applicationGet();
        }

        this.props.notesList();
    }

    public render(): JSX.Element {
        const { administrators, application, match, notes } = this.props;
        const { note } = this.state;

        if (!administrators || !application || !notes) {
            return (
                <Layout applicationUuid={match.params.applicationUuid} section='notes'>
                    <Spin/>
                </Layout>
            );
        }

        const unsavedNote: INote = _.find(notes, (loopNote: INote) => !loopNote.uuid);

        const unsavedNoteBlock: JSX.Element = unsavedNote && (
            <React.Fragment>
                <Typography.Text type='secondary'>{administrators[unsavedNote.administratorUuid].name}</Typography.Text><br/>
                <Typography.Text>{unsavedNote.note}</Typography.Text>
            </React.Fragment>
        );

        const notesBlock: JSX.Element[] = _.sortBy(_.filter(notes, (loopNote: INote) => !!loopNote.uuid), 'createdTime').map((loopNote: INote) => (
            <Timeline.Item key={loopNote.uuid} label={dayjs(loopNote.createdTime).format('YYYY-MM-DD - HH:mm')}>
                <Typography.Text type='secondary'>{administrators[loopNote.administratorUuid].name}</Typography.Text><br/>
                <Typography.Text>{loopNote.note}</Typography.Text>
            </Timeline.Item>
        ));

        return (
            <Layout applicationUuid={match.params.applicationUuid} section='notes'>
                <Typography.Title level={2}>Notes</Typography.Title>
                <Form.Item label='Note' className='note'>
                    <TextArea disabled={!!unsavedNote} onChange={this.onChangeNote} rows={15} value={note} />
                </Form.Item>
                <Button
                    className='add-note'
                    disabled={!note || !!unsavedNote}
                    onClick={this.onClickAddNote}
                    type='primary'
                >
                    Add Note
                </Button>
                {notesBlock.length > 0 && <Divider/>}
                <Timeline mode='left' pending={unsavedNoteBlock} reverse={true}>
                    {notesBlock}
                </Timeline>
            </Layout>
        );
    }

    private onChangeNote(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.setState({
            note: event.target.value,
        });
    }

    private onClickAddNote(): void {
        const { note } = this.state;

        this.setState({
            note: null,
        });

        this.props.noteAdd(note);
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        administrators: administratorsSelector(state),
        application: applicationSelector(state, ownProps.match.params.applicationUuid),
        notes: applicationNotesSelector(state, ownProps.match.params.applicationUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        administratorsList: () => dispatch(administratorsListAction()),
        applicationGet: () => dispatch(applicationGetAction(ownProps.match.params.applicationUuid)),
        noteAdd: (note: string) => dispatch(applicationNotesAddAction(ownProps.match.params.applicationUuid, note)),
        notesList: () => dispatch(applicationNotesListAction(ownProps.match.params.applicationUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Notes);
